import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { TbxTooltip } from '@/common/components'
import { MATERIAL_PROFILE_TYPES } from '@/common/utils'

const classes = {
    formControl_small: {
        width: '120px',
    },
    formControl_medium: {
        width: '180px',
    },
}

/**
 * Profile and profile dimensions selectors for rotary quote items.
 * Displays paired dropdowns for selecting profile type and dimensions.
 * Only rendered for rotary mode parts when tube options are enabled.
 * Memoized to prevent unnecessary re-renders when parent state changes.
 *
 * @param {Object} props - Component props
 * @param {string} props.profileValue - The currently selected profile value
 * @param {string} props.dimensionsValue - The currently selected profile dimensions value
 * @param {Array<string>} props.profileOptions - Available profile options (e.g., Circle, Square, Rectangle)
 * @param {Array<string>} props.dimensionsOptions - Available dimension options
 * @param {string} props.units - The unit of measurement (mm or in)
 * @param {boolean} [props.profileDisabled=false] - Whether the profile selector is disabled
 * @param {boolean} [props.dimensionsDisabled=false] - Whether the dimensions selector is disabled
 * @param {boolean} [props.isPartFromLibrary=false] - Whether the part is from the part library
 * @param {boolean} [props.isMaterialUnavailable=false] - Whether the material is unavailable for library parts
 * @param {Function} props.onProfileChange - Callback when profile selection changes
 * @param {Function} props.onDimensionsChange - Callback when dimensions selection changes
 * @returns {React.ReactElement} A row with profile and dimensions dropdowns
 */
const ProfileControls = ({
    dimensionsDisabled = false,
    dimensionsOptions,
    dimensionsValue,
    isMaterialUnavailable = false,
    isPartFromLibrary = false,
    onDimensionsChange,
    onProfileChange,
    profileDisabled = false,
    profileOptions,
    profileValue,
    units,
}) => {
    const { t } = useTranslation()

    const getTooltipTitle = () => {
        if (!isPartFromLibrary) return ''
        if (isMaterialUnavailable) {
            return t('Material for Part Library part is no longer available for selection')
        }
        return t('Cannot edit parts from library')
    }

    const tooltipTitle = getTooltipTitle()

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <Typography data-testid="nqp-quote-item-profile-label">{t('Profile')}</Typography>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'flex-end',
                    width: '100%',
                }}
            >
                <TbxTooltip
                    title={tooltipTitle}
                    arrow
                >
                    <FormControl
                        color="primary"
                        margin="none"
                        size="small"
                        sx={classes.formControl_small}
                        variant="outlined"
                    >
                        <Select
                            disabled={profileDisabled}
                            id="select-itemProfile"
                            inputProps={{ 'data-testid': 'nqp-quote-item-profile' }}
                            labelId="select-itemProfile-label"
                            SelectDisplayProps={{ style: { textAlign: 'left' } }}
                            value={profileValue}
                            onChange={(e) => onProfileChange(e)}
                        >
                            {profileOptions?.map((profile) => (
                                <MenuItem
                                    key={profile}
                                    value={profile}
                                >
                                    {MATERIAL_PROFILE_TYPES[profile]?.label || profile}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </TbxTooltip>
                <TbxTooltip
                    title={tooltipTitle}
                    arrow
                >
                    <FormControl
                        color="primary"
                        margin="none"
                        size="small"
                        sx={classes.formControl_medium}
                        variant="outlined"
                    >
                        <Select
                            disabled={dimensionsDisabled}
                            id="select-itemProfileDimensions"
                            inputProps={{ 'data-testid': 'nqp-quote-item-profile-dimensions' }}
                            labelId="select-itemProfileDimensions-label"
                            SelectDisplayProps={{ style: { textAlign: 'right' } }}
                            value={dimensionsValue}
                            onChange={(e) => onDimensionsChange(e)}
                        >
                            {dimensionsOptions?.map((dimension) => (
                                <MenuItem
                                    key={dimension}
                                    value={dimension}
                                >
                                    {dimension} {units}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </TbxTooltip>
            </Box>
        </Box>
    )
}

ProfileControls.propTypes = {
    dimensionsValue: PropTypes.string.isRequired,
    profileValue: PropTypes.string.isRequired,
    units: PropTypes.string.isRequired,
    onDimensionsChange: PropTypes.func.isRequired,
    onProfileChange: PropTypes.func.isRequired,
    dimensionsDisabled: PropTypes.bool,
    dimensionsOptions: PropTypes.arrayOf(PropTypes.string),
    isMaterialUnavailable: PropTypes.bool,
    isPartFromLibrary: PropTypes.bool,
    profileDisabled: PropTypes.bool,
    profileOptions: PropTypes.arrayOf(PropTypes.string),
}

export default memo(ProfileControls)
